import { useIsRestoring, useQuery } from "@tanstack/react-query";
import { LocationsApi, type SearchQueryRequest } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createOpenApiConfiguration } from "@/lib/utils";
import { publicLocationsScrollQueryKey } from "@/lib/utils/prefetch/seedPublicQueryData";

interface IUseLocationsGetProps {
  searchQueryRequest?: SearchQueryRequest | null;
  enabled?: boolean;
}

export const useLocationsGet = ({
  searchQueryRequest,
  enabled = true,
}: IUseLocationsGetProps = {}) => {
  const isRestoring = useIsRestoring();
  const useSearch =
    searchQueryRequest != null &&
    Array.isArray(searchQueryRequest.criteria) &&
    searchQueryRequest.criteria.length > 0;

  const { data, isError, isLoading, isSuccess, isPending, isFetching, error, refetch } = useQuery({
    // null (not undefined) keeps SSR dehydrate + client keys identical for the scroll path.
    queryKey: useSearch
      ? [QueryKeys.Locations, "search", searchQueryRequest]
      : publicLocationsScrollQueryKey,
    // Wait for SSR hydration so the navbar does not race and refetch locations.
    enabled: enabled && !isRestoring,
    queryFn: () => {
      const service = new LocationsApi(createOpenApiConfiguration());
      if (useSearch) {
        return service.apiLocationsSearchPost({
          page: 1,
          pageSize: 1000,
          searchQueryRequest,
        });
      }
      return service.apiLocationsScrollGet({
        page: 1,
        pageSize: 1000,
      });
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    isFetching,
    error,
    refetch,
  };
};
